import React from "react";
import { X } from "lucide-react";
import { endpoints } from "../lib/api";
import { TRACK_TAGS } from "../lib/tagMeta";

export const MAX_TAGS = 6;

const LINKS = [
  { key: "demo_url", label: "Live demo", ph: "https://your-app.com" },
  { key: "video_url", label: "Video", ph: "YouTube, Loom or Vimeo link", hint: "Embedded on your project page." },
  { key: "github_url", label: "GitHub", ph: "https://github.com/you/repo" },
  { key: "deck_url", label: "Pitch deck", ph: "Google Slides, Canva, PDF link…" },
  { key: "website_url", label: "Website", ph: "https://your-site.com" },
];

export const detailsPayload = (full) => {
  const trackTags = String(full.track || "").split(",").map((t) => `#${t.trim()}`).filter((t) => TRACK_TAGS.includes(t));
  const topics = (full.u_tags || []).filter((t) => !TRACK_TAGS.includes(t));
  const out = { team: full.team, u_tags: [...trackTags, ...topics] };
  LINKS.forEach(({ key }) => { out[key] = (full[key] || "").trim(); });
  return out;
};

export const saveErrorText = (ex) => {
  const d = ex?.response?.data?.detail || "";
  if (d.startsWith("invalid_url")) {
    const f = LINKS.find((l) => d.includes(l.key));
    return `That ${f ? f.label.toLowerCase() : ""} link doesn't look right. Check it and try again.`;
  }
  if (d === "too_many_tags") return `Pick up to ${MAX_TAGS} tags.`;
  if (d.startsWith("too_long")) return `The ${d.split(": ")[1]?.replace("_", " ")} is too long. Try trimming it.`;
  if (d.startsWith("invalid_field")) return `Please check the ${d.split(": ")[1]?.replace("_", " ")} field.`;
  return "Save failed";
};

export function ProjectDetailsFields({ full, setFull, inp, prefix }) {
  const [allTags, setAllTags] = React.useState([]);
  React.useEffect(() => { endpoints.tags().then((t) => setAllTags(t.filter((x) => x.group !== "track"))).catch(() => {}); }, []);
  const topics = (full.u_tags || []).filter((t) => !TRACK_TAGS.includes(t));
  const toggle = (tag) => {
    const on = topics.includes(tag);
    if (!on && topics.length >= MAX_TAGS) return;
    setFull({ ...full, u_tags: on ? full.u_tags.filter((t) => t !== tag) : [...(full.u_tags || []), tag] });
  };

  return (
    <div className="space-y-3">
      <label className="block">
        <span className="mb-1 block text-[13px] font-semibold text-ink-900">Team name</span>
        <input className={inp} value={full.team || ""} onChange={(e) => setFull({ ...full, team: e.target.value })} data-testid={`${prefix}-team`} />
      </label>
      <div className="rounded-md border border-line-200 bg-white p-3">
        <span className="block text-[13px] font-semibold text-ink-900">Links</span>
        <p className="mb-2 text-[11px] text-ink-400">Leave blank to hide. We'll add https:// if you skip it.</p>
        <div className="space-y-2">
          {LINKS.map((l) => (
            <label key={l.key} className="block">
              <span className="mb-0.5 block text-[12px] font-semibold text-ink-600">{l.label}{l.hint && <span className="font-normal text-ink-400"> · {l.hint}</span>}</span>
              <input type="url" inputMode="url" className={inp} placeholder={l.ph} value={full[l.key] || ""}
                onChange={(e) => setFull({ ...full, [l.key]: e.target.value })} data-testid={`${prefix}-${l.key.replace("_url", "")}-url`} />
            </label>
          ))}
        </div>
      </div>
      <div className="rounded-md border border-line-200 bg-white p-3" data-testid={`${prefix}-tags`}>
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-[13px] font-semibold text-ink-900">Tags</span>
          <span className="text-[11px] text-ink-400" data-testid={`${prefix}-tags-count`}>{topics.length}/{MAX_TAGS} · track tag is added automatically</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {allTags.map((t) => {
            const on = topics.includes(t.tag);
            const full_ = !on && topics.length >= MAX_TAGS;
            return (
              <button type="button" key={t.tag} onClick={() => toggle(t.tag)} disabled={full_} title={t.description}
                data-testid={`${prefix}-tag-${t.tag.replace("#", "")}`} aria-pressed={on}
                className={`inline-flex items-center gap-1 rounded-pill px-2.5 py-1 text-[12px] font-semibold transition-colors ${on ? "bg-navy-800 text-white" : "bg-navy-100 text-navy-800 hover:bg-navy-200"} disabled:opacity-40`}>
                {t.tag}{on && <X size={11} />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
